import { useState } from 'react'

const COOKIE_NAME = 'dark_mode'
const COOKIE_DAYS = 365

function applyDarkClass(dark: boolean) {
  document.documentElement.classList.toggle('dark', dark)
  // Guarded for the same reason as the inline script in `shared/_head.html.erb`,
  // which runs in <head> before there is a body.
  document.body?.classList.toggle('dark', dark)
}

function persistPreference(dark: boolean) {
  const expires = new Date(Date.now() + COOKIE_DAYS * 24 * 60 * 60 * 1000)
  document.cookie = `${COOKIE_NAME}=${dark};path=/;SameSite=Lax;expires=${expires.toUTCString()}`
}

/**
 * Shared dark-mode state for every nav surface.
 *
 * The inline script in `shared/_head.html.erb` already resolves the cookie and
 * `prefers-color-scheme` before first paint, so this only reads back what it
 * decided. Re-deriving the preference here would let the two disagree.
 *
 * Read during initialization rather than in an effect: the class is already on
 * <html> by the time React mounts, and seeding `false` would render every toggle
 * in the light state for one frame on a dark page.
 */
export function useDarkMode(): [boolean, (dark: boolean) => void] {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'))

  const setDarkMode = (dark: boolean) => {
    setIsDark(dark)
    applyDarkClass(dark)
    persistPreference(dark)
  }

  return [isDark, setDarkMode]
}
